import { KeyRound, PencilIcon, TrashIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import API from '@/lib/api'
import { formatDate } from '@/lib/formats/formatDate'
import { formatTime } from '@/lib/formats/formatTime'

export type ApiTokenListItem = {
  id: number | string
  name: string
  createdAt: string
}

export type ApiTokenCreated = ApiTokenListItem & {
  token: string
}

const tokenNameInputClassName = 'h-8'

export const ApiTokens = ({
  tokens,
  mutate,
  creating = false,
  onCreatingChange,
}: {
  tokens: ApiTokenListItem[]
  mutate: () => void
  /** Řízeno zvenku: tlačítko Nový token je v záhlaví sekce. */
  creating?: boolean
  onCreatingChange?: (creating: boolean) => void
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const [selectedToken, setSelectedToken] = useState<number | string | null>(
    null,
  )
  const [revealOpen, setRevealOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | string | 'new' | null>(
    null,
  )
  const [name, setName] = useState('')
  const [createdToken, setCreatedToken] = useState<ApiTokenCreated | null>(
    null,
  )
  const [isSaving, setIsSaving] = useState(false)

  const cancelEdit = () => {
    setEditingId(null)
    setName('')
    onCreatingChange?.(false)
  }

  useEffect(() => {
    if (creating) {
      setEditingId('new')
      setName('')
    }
  }, [creating])

  const startRename = (token: ApiTokenListItem) => {
    setEditingId(token.id)
    setName(token.name)
  }

  const onCreate = async () => {
    const trimmedName = name.trim()

    if (!trimmedName) {
      toast.error(t('api.tokens.form.name.errors.required'))
      return
    }

    setIsSaving(true)

    return API.post<ApiTokenCreated>('/api/v2/settings/api', {
      name: trimmedName,
    })
      .then((response) => {
        cancelEdit()
        setCreatedToken(response.data)
        setRevealOpen(true)
        toast.success(t('api.tokens.actions.create.success'))
      })
      .catch(() => {
        toast.error(t('api.tokens.actions.create.error'))
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const onRevealClose = (open: boolean) => {
    setRevealOpen(open)

    if (!open) {
      setCreatedToken(null)
      mutate()
    }
  }

  const onRename = async () => {
    if (editingId === null || editingId === 'new') {
      return
    }

    const trimmedName = name.trim()

    if (!trimmedName) {
      toast.error(t('api.tokens.form.name.errors.required'))
      return
    }

    setIsSaving(true)

    return API.patch(`/api/v2/settings/api/${editingId}`, {
      name: trimmedName,
    })
      .then(() => {
        cancelEdit()
        mutate()
        toast.success(t('api.tokens.actions.rename.success'))
      })
      .catch(() => {
        toast.error(t('api.tokens.actions.rename.error'))
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const onSave = () => {
    if (editingId === 'new') {
      return onCreate()
    }

    return onRename()
  }

  const onNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onSave()
    }

    if (event.key === 'Escape') {
      cancelEdit()
    }
  }

  const onDelete = async (tokenId: number | string) => {
    return API.delete(`/api/v2/settings/api/${tokenId}`)
      .then(() => {
        setSelectedToken(null)
        mutate()
        toast.success(t('api.tokens.actions.delete.success'))
      })
      .catch(() => {
        toast.error(t('api.tokens.actions.delete.error'))
      })
  }

  const editingRowActions = (label: string) => (
    <div className="flex justify-end gap-1">
      <Button size="sm" onClick={onSave} disabled={isSaving}>
        {label}
      </Button>
      <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={isSaving}>
        {tg('button.cancel')}
      </Button>
    </div>
  )

  return (
    <>
      <Dialog open={revealOpen} onOpenChange={onRevealClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('api.tokens.reveal.title')}</DialogTitle>
            <DialogDescription>{t('api.tokens.reveal.description')}</DialogDescription>
          </DialogHeader>
          {createdToken && (
            <div className="flex items-start gap-2 rounded-md border bg-muted p-2 pl-3">
              <code className="min-w-0 flex-1 py-1.5 font-mono text-sm break-all">{createdToken.token}</code>
              <CopyButton content={createdToken.token} variant="outline" size="icon" />
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => onRevealClose(false)}>
              {t('api.tokens.reveal.dismiss')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tokens.length === 0 && editingId !== 'new' ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center">
          <KeyRound className="size-5 text-muted-foreground" />
          <p className="text-sm font-medium">Zatím žádné tokeny</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Token vytvoříte tlačítkem Nový token. Zobrazí se jen jednou, hned po vytvoření.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t('api.tokens.table.name')}</TableHead>
                <TableHead className="hidden w-44 md:table-cell">{t('api.tokens.table.createdAt')}</TableHead>
                <TableHead className="w-px md:w-44" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {editingId === 'new' && (
                <TableRow className="hover:bg-transparent">
                  <TableCell>
                    <Input
                      className={tokenNameInputClassName}
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      onKeyDown={onNameKeyDown}
                      placeholder={t('api.tokens.form.name.placeholder')}
                      aria-label={t('api.tokens.form.name.label')}
                      autoComplete="off"
                      autoFocus
                    />
                  </TableCell>
                  <TableCell className="hidden md:table-cell" />
                  <TableCell>{editingRowActions('Vytvořit')}</TableCell>
                </TableRow>
              )}
              {tokens.map((token) => (
                <TableRow key={token.id} className="group">
                  <TableCell className="w-full max-w-0 md:w-auto md:max-w-none">
                    {editingId === token.id ? (
                      <Input
                        className={tokenNameInputClassName}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        onKeyDown={onNameKeyDown}
                        placeholder={t('api.tokens.form.name.placeholder')}
                        aria-label={t('api.tokens.form.name.label')}
                        autoComplete="off"
                        autoFocus
                      />
                    ) : (
                      <>
                        <span className="block truncate font-medium md:inline">{token.name}</span>
                        <p className="truncate text-xs text-muted-foreground tabular md:hidden">
                          {formatDate(token.createdAt)} {formatTime(token.createdAt)}
                        </p>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground tabular md:table-cell">
                    {formatDate(token.createdAt)} {formatTime(token.createdAt)}
                  </TableCell>
                  <TableCell>
                    {editingId === token.id ? (
                      editingRowActions(tg('button.save'))
                    ) : (
                      <div className="flex justify-end gap-1 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('api.tokens.actions.rename.title')}
                          onClick={() => startRename(token)}
                          disabled={editingId !== null}
                        >
                          <PencilIcon />
                        </Button>
                        <ConfirmButton
                          title={t('api.tokens.actions.delete.title')}
                          description={t('api.tokens.actions.delete.description')}
                          open={selectedToken === token.id}
                          onOpenChange={(open: boolean) => {
                            if (!open) {
                              setSelectedToken(null)
                            }
                          }}
                          onConfirm={() => onDelete(token.id)}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="hover:text-danger"
                            aria-label={t('api.tokens.actions.delete.title')}
                            onClick={() => setSelectedToken(token.id)}
                            disabled={editingId !== null}
                          >
                            <TrashIcon />
                          </Button>
                        </ConfirmButton>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}
